<template>
  <div>
    <sui-message info>
      <span v-html="description" />
    </sui-message>

    <input
      ref="fileInput"
      type="file"
      accept=".xlsx,.xls,.csv"
      @change="onFile"
    >

    <div
      v-if="preview"
      style="margin-top: 14px"
    >
      <div class="counts">
        <sui-label color="green">OK {{ preview.counts.ok }}</sui-label>
        <sui-label color="yellow">Avisos {{ preview.counts.warnings }}</sui-label>
        <sui-label color="red">Errores {{ preview.counts.errors }}</sui-label>
        <sui-label basic>Total {{ preview.counts.total }}</sui-label>
        <sui-button
          size="tiny"
          :loading="loading"
          @click="runPreview"
        >
          Revalidar
        </sui-button>
        <sui-button
          v-if="(aiHint || aiMapTo) && preview.counts.errors > 0"
          size="tiny"
          color="purple"
          :loading="aiLoading"
          @click="fixWithAI"
        >
          <sui-icon name="magic" /> Arreglar con IA
        </sui-button>
      </div>
      <div class="edit-hint">
        Puedes corregir los campos requeridos aquí mismo y pulsar <b>Revalidar</b>.
      </div>

      <div
        v-if="bulkColumns.length"
        class="bulk-bar"
      >
        <span class="bulk-title">Aplicar a todas las filas:</span>
        <div
          v-for="col in bulkColumns"
          :key="col.key"
          class="bulk-item"
        >
          <input
            v-model="bulkValues[col.key]"
            :list="colOptions[col.key] ? 'dl_' + col.key : undefined"
            :placeholder="col.label"
          >
          <sui-button
            size="tiny"
            :loading="loading"
            @click="applyBulk(col.key)"
          >
            Aplicar {{ col.label }}
          </sui-button>
        </div>
      </div>

      <datalist
        v-for="col in columns"
        :id="'dl_' + col.key"
        :key="'dl_' + col.key"
      >
        <option
          v-for="(o, oi) in (colOptions[col.key] || [])"
          :key="oi"
          :value="o.value"
        />
      </datalist>

      <div style="overflow-x: auto">
        <table class="ui celled compact small table import-table">
          <thead>
            <tr>
              <th>#</th>
              <th
                v-for="col in columns"
                :key="col.key"
              >
                {{ col.label }}
              </th>
              <th>Estado</th>
              <th>Mensajes</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in preview.rows"
              :key="row.i"
            >
              <td>{{ row.i }}</td>
              <td
                v-for="col in columns"
                :key="col.key"
              >
                <input
                  v-model="rows[row.i - 1][col.key]"
                  :list="colOptions[col.key] ? 'dl_' + col.key : undefined"
                >
              </td>
              <td>
                <sui-label
                  :color="statusColor(row.status)"
                  size="tiny"
                >{{ statusLabel(row.status) }}</sui-label>
              </td>
              <td>
                <div
                  v-for="(m, idx) in row.messages"
                  :key="idx"
                  :style="{ color: m.level === 'error' ? '#c00' : '#b58105' }"
                >
                  {{ m.text }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <sui-message
        v-if="committed !== null"
        positive
      >
        Importación completada: <b>{{ committed }}</b> registros creados.
      </sui-message>
      <sui-button
        positive
        :loading="loading"
        :disabled="loading || committed !== null || importable === 0"
        @click="commit"
      >
        Confirmar importación ({{ importable }} filas)
      </sui-button>
      <span class="hint">Las {{ preview.counts.errors }} filas con error se omitirán.</span>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useToast } from "vue-toastification";
import { read, utils } from "xlsx";
import Api from "../../api/Api";

const props = defineProps({
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    importFn: { type: Function, required: true },
    columns: { type: Array, required: true }, // [{ key, label }]
    sheet: { type: String, default: "" }, // sheet name to read from a multi-sheet workbook (else first sheet)
    aiHint: { type: String, default: "" }, // when set, shows an "Arreglar con IA" button; describes field rules
    aiMapTo: { type: String, default: "" }, // when set (e.g. "product"), AI also maps references to existing records
});
const emit = defineEmits(["done"]);

const toast = useToast();
const loading = ref(false);
const rows = ref([]);
const preview = ref(null);
const committed = ref(null);
const fileInput = ref(null);

const importable = computed(() =>
    preview.value ? preview.value.counts.ok + preview.value.counts.warnings : 0,
);

// Autocomplete options per column (static `options` or async `optionsFn`) + bulk-apply.
const colOptions = reactive({});
const bulkValues = reactive({});
const bulkColumns = computed(() => props.columns.filter((c) => c.bulk));

onMounted(async () => {
    for (const col of props.columns) {
        if (col.options) colOptions[col.key] = col.options;
        else if (col.optionsFn) {
            try { colOptions[col.key] = await col.optionsFn(); } catch (e) { colOptions[col.key] = []; }
        }
    }
});

// Fill one column on every parsed row with the chosen value, then re-validate.
const applyBulk = async (key) => {
    const v = bulkValues[key] ?? "";
    rows.value.forEach((r) => { r[key] = v; });
    if (preview.value) await runPreview();
};

// Send the error rows (values + messages) to a free LLM, apply the suggested {row,field,value}
// fixes to the editable cells, then re-validate. The user reviews the result before confirming.
const aiLoading = ref(false);
const fixWithAI = async () => {
    if (!preview.value) return;
    const keys = props.columns.map((c) => c.key);
    const payload = preview.value.rows
        .filter((r) => r.status === "error")
        .map((r) => {
            const src = rows.value[r.i - 1] || {};
            const values = {};
            keys.forEach((k) => { values[k] = src[k]; });
            return { row: r.i, values, errors: r.messages.map((m) => m.text) };
        });
    if (!payload.length) return;
    aiLoading.value = true;
    try {
        const res = await Api.aiFixImport({ hint: props.aiHint, rows: payload, mapTo: props.aiMapTo });
        if (res.error) return toast.error(res.message_client);
        const fixes = (res.data && res.data.fixes) || [];
        let applied = 0;
        for (const f of fixes) {
            const target = rows.value[f.row - 1];
            if (target && keys.includes(f.field)) { target[f.field] = f.value; applied++; }
        }
        toast.success(`IA aplicó ${applied} correcciones. Revisa y confirma.`);
        await runPreview();
    } catch (e) {
        toast.error("No se pudo corregir con IA.");
    } finally {
        aiLoading.value = false;
    }
};
const statusColor = (s) => ({ ok: "green", warn: "yellow", error: "red", skipped: "grey" }[s] || "grey");
const statusLabel = (s) => ({ ok: "OK", warn: "Aviso", error: "Error", skipped: "Omitido" }[s] || s);

const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    committed.value = null;
    try {
        const wb = read(await file.arrayBuffer());
        // If the user uploaded the combined multi-sheet workbook, read this catalog's own sheet;
        // otherwise fall back to the first sheet (a single-catalog file).
        const sheetName = props.sheet && wb.Sheets[props.sheet] ? props.sheet : wb.SheetNames[0];
        rows.value = utils.sheet_to_json(wb.Sheets[sheetName], { defval: "" });
        if (!rows.value.length) return toast.error("El archivo no tiene filas.");
        await runPreview();
    } catch (err) {
        toast.error("No se pudo leer el archivo.");
    }
};

const runPreview = async () => {
    loading.value = true;
    committed.value = null;
    try {
        const response = await props.importFn({ rows: rows.value, commit: false });
        if (response.error) return toast.error(response.message_client);
        preview.value = response.data;
    } catch (err) {
        toast.error("No se pudo validar el archivo.");
    } finally {
        loading.value = false;
    }
};

const commit = async () => {
    loading.value = true;
    try {
        const response = await props.importFn({ rows: rows.value, commit: true });
        if (response.error) return toast.error(response.message_client);
        preview.value = response.data;
        committed.value = response.data.counts.created || 0;
        toast.success(`Importación completada: ${committed.value} registros creados.`);
        emit("done", committed.value);
    } catch (err) {
        toast.error("No se pudo completar la importación.");
    } finally {
        loading.value = false;
    }
};
</script>

<style scoped>
.counts {
    margin-bottom: 8px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
}
.edit-hint { margin-bottom: 12px; color: #777; font-size: 12px; }
.bulk-bar {
    display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
    margin-bottom: 12px; padding: 8px 10px; background: #f7f7f9; border-radius: 6px;
}
.bulk-title { font-size: 12px; color: #555; font-weight: 600; }
.bulk-item { display: flex; align-items: center; gap: 6px; }
.bulk-item input { padding: 4px 6px; border: 1px solid #ddd; border-radius: 4px; font-size: 12px; }
.hint { display: block; margin-top: 8px; color: #777; font-size: 12px; }
.import-table input {
    width: 100%;
    min-width: 90px;
    padding: 4px 6px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 12px;
}
input[type="file"] { display: block; }
</style>
